/* =============================================================================
 * Arachne Audio - Unified cart drawer grouping
 *
 * Collapses each configured cable (one base line + N hidden add-on lines that
 * share a properties._config_id) into ONE visual card inside the native Dawn
 * cart drawer. Standalone products render as ordinary single rows in the SAME
 * drawer. Atomic remove, atomic quantity, inline undo (no confirm dialog),
 * Edit (reopen the configurator pre-filled) and Keep configuring.
 *
 * Vanilla JS, ES5-style, no framework, no build step, no deps. Plain helpers are
 * exported for Node tests; the DOM controller runs only in a browser.
 *
 * FAIL-SAFE CONTRACT (learned the hard way): every render path is wrapped in
 * try/catch. On ANY error or missing data the drawer falls back to the stock
 * flat cart line list (the existing Liquid markup is never removed, only hidden
 * once grouping succeeds), so the cart can never white-screen or lose function.
 *
 * Cart contract (read-only here, never rewritten): the configurator submits a
 * base line with properties { _config_id, _model, <Globo option keys...> } and
 * one hidden line per priced option with { _config_id, _hidden:true }. Shopify
 * stores property values as strings, so _hidden arrives as the string "true".
 * ========================================================================== */
(function () {
  "use strict";

  /* Idempotent include: this file is loaded by the drawer snippet AND (on this
   * drawer-mode store) by the /cart page section, so on /cart it executes twice.
   * A second classic-script execution must NOT redefine the API or re-bind the
   * controller's document listeners. Bail once the API is already on window.
   * Node (no window) always proceeds so module.exports works for the test suite. */
  if (typeof window !== "undefined" && window.AcfgCartGroups) return;

  /* ============================ pure helpers ============================== *
   * DOM-free, deterministic, and unit-tested in .build/cart-groups.test.js.   */

  function propIsHidden(props) {
    if (!props) return false;
    var v = props._hidden;
    return v === true || v === "true" || v === "1" || v === 1;
  }

  function configIdOf(props) {
    if (!props) return null;
    var v = props._config_id;
    return v == null || v === "" ? null : String(v);
  }

  // The visible option pairs of a base line: every property whose key does not
  // start with "_", in submission order. Values are kept exactly as stored.
  function optionPairs(props) {
    var out = [];
    if (!props) return out;
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (k.charAt(0) === "_") continue;
      var v = props[k];
      if (v == null || String(v).length === 0) continue;
      out.push({ key: k, value: String(v) });
    }
    return out;
  }

  // Decode the handful of HTML entities Shopify/Globo can emit in titles and
  // option labels (e.g. "Meze 109 &amp; 99" -> "Meze 109 & 99"). DOM-free so it
  // also runs under Node tests. Output is always inserted via textContent, so
  // decoding here introduces no injection risk.
  function decodeEntities(s) {
    return String(s).replace(/&(amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9a-fA-F]+);/g, function (m, e) {
      switch (e) {
        case "amp": return "&";
        case "lt": return "<";
        case "gt": return ">";
        case "quot": return "\"";
        case "apos": return "'";
        case "nbsp": return "\u00a0";
        default:
          var code = e.charAt(1) === "x" || e.charAt(1) === "X"
            ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
          if (isNaN(code)) return m;
          // fromCodePoint handles astral-plane entities (emoji in titles);
          // fromCharCode would emit a lone surrogate for those.
          return String.fromCodePoint ? String.fromCodePoint(code) : String.fromCharCode(code);
      }
    });
  }

  // Trim Globo's leading/trailing whitespace + U+2028/U+2029 line separators and
  // decode HTML entities for DISPLAY only. The underlying cart value is never
  // modified by this.
  function displayValue(v) {
    return decodeEntities(String(v == null ? "" : v).replace(/\s+/g, " ").trim());
  }

  // Build a compact spec: each option value as-is, joined by " . ". Consecutive
  // colour-keyed properties are folded into one "/"-joined segment so a 4-strand
  // cable reads as one colour group instead of four near-identical entries.
  function specSegments(pairs) {
    var segs = [];
    var colourRun = null;
    for (var i = 0; i < (pairs ? pairs.length : 0); i++) {
      var k = pairs[i].key;
      var v = displayValue(pairs[i].value);
      if (!v) continue;
      if (/colou?r/i.test(k)) {
        // Globo colour labels carry a strand-count prefix ("1 Black") — noise in
        // the compact spec (read "1 Black / 1 Black / 1 Black" in the cart).
        // Strip it for DISPLAY only; the cart value itself is untouched.
        v = v.replace(/^\d+\s+/, "");
        if (!v) continue;
        if (!colourRun) { colourRun = []; segs.push(colourRun); }
        colourRun.push(v);
      } else {
        colourRun = null;
        segs.push([v]);
      }
    }
    var out = [];
    for (var j = 0; j < segs.length; j++) {
      var seg = segs[j];
      // An all-identical run collapses to one value ("Black / Black / Black" → "Black").
      var allSame = true;
      for (var s = 1; s < seg.length; s++) if (seg[s] !== seg[0]) { allSame = false; break; }
      out.push(allSame ? seg[0] : seg.join(" / "));
    }
    return out;
  }

  function lineCents(line) {
    var v = line ? line.final_line_price : 0;
    if (typeof v === "number") return v;
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }
  function origCents(line) {
    var v = line ? line.original_line_price : 0;
    if (typeof v === "number") return v;
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  // Group a flat list of cart lines into an ordered view:
  //   { type:"product", line }                  - standalone product (no _config_id)
  //   { type:"group", configId, lines:[...], base, model, options:[{key,value}],
  //     totalCents, originalCents, quantity }    - one configured cable
  // First-appearance order is preserved. Hidden add-on lines are folded into
  // their group and NEVER surface as their own item.
  function groupLines(lines) {
    var out = [];
    var byId = {};
    for (var i = 0; i < (lines ? lines.length : 0); i++) {
      var line = lines[i] || {};
      var props = line.properties || {};
      var cid = configIdOf(props);
      if (!cid) { out.push({ type: "product", line: line }); continue; }
      var g = byId[cid];
      if (!g) {
        g = byId[cid] = {
          type: "group", configId: cid, lines: [], base: null,
          model: "", options: [], totalCents: 0, originalCents: 0, quantity: 0
        };
        out.push(g);
      }
      g.lines.push(line);
      g.totalCents += lineCents(line);
      g.originalCents += origCents(line);
      if (!propIsHidden(props) && !g.base) {
        g.base = line;
        g.model = displayValue(props._model || line.product_title || line.title || "");
        g.options = optionPairs(props);
        g.quantity = line.quantity || 1;
      }
    }
    // Defensive: a group with no surviving non-hidden base line still renders
    // (use its first line) rather than vanishing or leaking add-on rows.
    for (var j = 0; j < out.length; j++) {
      var item = out[j];
      if (item.type === "group" && !item.base && item.lines.length) {
        var first = item.lines[0];
        item.base = first;
        item.model = displayValue((first.properties && first.properties._model) || first.title || "");
        item.options = optionPairs(first.properties || {});
        item.quantity = first.quantity || 1;
      }
    }
    return out;
  }

  // Atomic update maps, keyed by line item KEY (index-stable, unlike line index).
  function buildRemoveUpdates(group) {
    var u = {};
    for (var i = 0; i < group.lines.length; i++) u[group.lines[i].key] = 0;
    return u;
  }
  function buildSetQtyUpdates(group, qty) {
    // Shopify MERGES byte-identical add-on lines (same variant + same props —
    // possible when two options resolve to the same connector variant) into ONE
    // line whose quantity is a multiple of the group's. A flat assignment would
    // undercount those merged lines on +/- (audit 2026-07-14 #12). Scale each
    // line by its per-unit multiplicity instead.
    var cur = (group.base && group.base.quantity) || group.quantity || 1;
    if (cur < 1) cur = 1;
    var u = {};
    for (var i = 0; i < group.lines.length; i++) {
      var l = group.lines[i];
      var unit = Math.max(1, Math.round((l.quantity || 1) / cur));
      u[l.key] = unit * qty;
    }
    return u;
  }

  // Exact re-add payload for undo: every line with its id, quantity, and an
  // unmodified copy of its properties (so _config_id, _hidden and the untrimmed
  // Globo option labels all round-trip and the cable regroups identically).
  function buildUndoItems(group) {
    var items = [];
    for (var i = 0; i < group.lines.length; i++) {
      var l = group.lines[i];
      var props = l.properties || {};
      var copy = {};
      var keys = Object.keys(props);
      for (var k = 0; k < keys.length; k++) copy[keys[k]] = props[keys[k]];
      items.push({ id: l.variant_id, quantity: l.quantity, properties: copy });
    }
    return items;
  }

  // Canonical Shopify money formatter (handles every money_format placeholder).
  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var fmt = format || "&euro;{{amount}}";
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? "," : thousands;
      decimal = decimal == null ? "." : decimal;
      if (isNaN(number) || number == null) return "0";
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : "";
      return dollars + centsPart;
    }

    var match = fmt.match(placeholder);
    var value = "";
    if (match) {
      switch (match[1]) {
        case "amount": value = withDelimiters(cents, 2); break;
        case "amount_no_decimals": value = withDelimiters(cents, 0); break;
        case "amount_with_comma_separator": value = withDelimiters(cents, 2, ".", ","); break;
        case "amount_no_decimals_with_comma_separator": value = withDelimiters(cents, 0, ".", ","); break;
        case "amount_with_apostrophe_separator": value = withDelimiters(cents, 2, "'", "."); break;
        case "amount_no_decimals_with_space_separator": value = withDelimiters(cents, 0, " ", ""); break;
        case "amount_with_space_separator": value = withDelimiters(cents, 2, " ", ","); break;
        case "amount_with_period_and_space_separator": value = withDelimiters(cents, 2, " ", "."); break;
        default: value = withDelimiters(cents, 2);
      }
      return fmt.replace(placeholder, value);
    }
    return withDelimiters(cents, 2);
  }

  var API = {
    propIsHidden: propIsHidden,
    configIdOf: configIdOf,
    optionPairs: optionPairs,
    specSegments: specSegments,
    groupLines: groupLines,
    buildRemoveUpdates: buildRemoveUpdates,
    buildSetQtyUpdates: buildSetQtyUpdates,
    buildUndoItems: buildUndoItems,
    formatMoney: formatMoney
  };
  /* Shared with the /cart PAGE controller (assets/cart-page-groups.js): the DOM
   * card renderers + a couple of DOM/display primitives. They are function
   * declarations (hoisted, defined in the controller section below), use a
   * `document` only at call time, and reference NO drawer-specific state, so the
   * page controller renders byte-identical cards against its own data island.
   * Attaching them here puts them on the exported API in every environment (they
   * are simply uncallable under plain Node, which never invokes them). */
  API.displayValue = displayValue;
  API.el = el;
  API.txt = txt;
  API.renderView = renderView;
  API.renderGroup = renderGroup;
  API.renderProduct = renderProduct;

  if (typeof module !== "undefined" && module.exports) module.exports = API;
  if (typeof window !== "undefined") window.AcfgCartGroups = API;

  /* ========================== DOM controller ============================== *
   * Browser only. Everything below is skipped under Node (test harness).      */
  if (typeof document === "undefined" || !document.querySelector) return;

  var UNDO_MS = 6000;
  var SECTIONS = ["cart-drawer", "cart-icon-bubble"];
  var TRASH_SVG =
    '<svg class="aa-trash" viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">' +
    '<path d="M6.2 1.5h3.6a.7.7 0 0 1 .7.7V3H5.5v-.8a.7.7 0 0 1 .7-.7Z" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
    '<path d="M2.8 3.6h10.4M12.3 3.6l-.6 9a1.2 1.2 0 0 1-1.2 1.1H5.5a1.2 1.2 0 0 1-1.2-1.1l-.6-9" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>' +
    '<path d="M6.6 6.2v5M9.4 6.2v5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';

  var observer = null;
  var enhanceQueued = false;
  var busy = false;
  var currentView = [];
  var currentFormat = "";
  var undoState = null;     // { items, model, timer }

  function routeUrl(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function txt(s) { return document.createTextNode(s == null ? "" : String(s)); }

  /* --------------------------- localized strings --------------------------- *
   * Customer-facing UI strings come from a JSON island emitted by the Liquid
   * that loads this script (<script type="application/json" data-aa-cart-strings>,
   * translated via locales/*.json cart_groups.*). Read once, lazily, with a
   * hard fallback to the English literals this file used to hard-code, so a
   * missing/broken island can never break rendering. %{model}/%{title}
   * placeholders are filled by the mini-interpolator below.                    */
  var AA_STRING_DEFAULTS = {
    edit: "Edit",
    build_another: "Build another",
    remove: "Remove",
    undo: "Undo",
    removed_model: "Removed %{model}.",
    qty_decrease: "Decrease quantity",
    qty_increase: "Increase quantity",
    remove_title: "Remove %{title}"
  };
  var aaStrings = null;
  function aaT(key, vars) {
    if (!aaStrings) {
      aaStrings = {};
      try {
        var node = document.querySelector("script[data-aa-cart-strings]");
        if (node) aaStrings = JSON.parse(node.textContent || "{}") || {};
      } catch (e) { aaStrings = {}; }
    }
    var s = aaStrings[key];
    if (typeof s !== "string" || !s) s = AA_STRING_DEFAULTS[key] || "";
    if (vars) {
      for (var k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          var v = vars[k] == null ? "" : String(vars[k]);
          // Two token forms: %{k} (locale-file convention, used by the fallback
          // defaults) and @@K@@ (the Liquid island passes this brace-free sentinel
          // through `t:` — literal %{...} braces break Shopify's tag parser).
          s = s.split("@@" + k.toUpperCase() + "@@").join(v);
          s = s.split("%{" + k + "}").join(v);
        }
      }
    }
    return s;
  }

  function announce(message) {
    var live = document.getElementById("CartDrawer-LiveRegionText");
    if (live) { live.setAttribute("aria-hidden", "false"); live.textContent = message; }
  }
  function showError(message) {
    var box = document.getElementById("CartDrawer-CartErrors");
    if (box) box.textContent = message || "";
  }

  /* ----------------------------- rendering -------------------------------- */
  function renderView(view, format, opts) {
    var wrap = document.createDocumentFragment();
    for (var i = 0; i < view.length; i++) {
      var item = view[i];
      wrap.appendChild(item.type === "group" ? renderGroup(item, format, opts) : renderProduct(item, format));
    }
    return wrap;
  }

  function priceBlock(finalCents, originalCents, format, cls) {
    var box = el("div", "aa-price" + (cls ? " " + cls : ""));
    if (originalCents && originalCents !== finalCents) {
      box.appendChild(el("s", "aa-price__was", formatMoney(originalCents, format)));
    }
    box.appendChild(el("span", "aa-price__now", formatMoney(finalCents, format)));
    return box;
  }

  function qtyStepper(qty, scope, id) {
    var box = el("div", "aa-qty");
    var dec = el("button", "aa-qty__btn");
    dec.type = "button";
    dec.setAttribute("data-aa-action", scope === "group" ? "group-dec" : "line-dec");
    dec.setAttribute(scope === "group" ? "data-aa-cid" : "data-aa-key", id);
    dec.setAttribute("aria-label", aaT("qty_decrease"));
    dec.innerHTML = "&minus;";
    if (qty <= 1) dec.disabled = true;
    var val = el("span", "aa-qty__val");
    val.setAttribute("aria-live", "polite");
    val.appendChild(txt(qty));
    var inc = el("button", "aa-qty__btn");
    inc.type = "button";
    inc.setAttribute("data-aa-action", scope === "group" ? "group-inc" : "line-inc");
    inc.setAttribute(scope === "group" ? "data-aa-cid" : "data-aa-key", id);
    inc.setAttribute("aria-label", aaT("qty_increase"));
    inc.innerHTML = "+";
    box.appendChild(dec); box.appendChild(val); box.appendChild(inc);
    return box;
  }

  function removeLink(action, idAttr, id, label) {
    var b = el("button", "aa-remove");
    b.type = "button";
    b.setAttribute("data-aa-action", action);
    b.setAttribute(idAttr, id);
    b.setAttribute("aria-label", label);
    b.innerHTML = TRASH_SVG;
    var lbl = el("span", "aa-remove__label");
    lbl.appendChild(txt(aaT("remove")));
    b.appendChild(lbl);
    return b;
  }

  function mediaNode(item) {
    var line = item.type === "group" ? item.base : item.line;
    var media = el("div", "aa-media");
    if (line && line.image) {
      var img = document.createElement("img");
      img.className = "aa-media__img";
      img.src = line.image;
      img.alt = "";
      img.loading = "lazy";
      img.width = 64; img.height = 64;
      media.appendChild(img);
    }
    return media;
  }

  function renderGroup(group, format, opts) {
    var showKeep = !(opts && opts.hideKeep);
    var card = el("div", "aa-card aa-card--group");
    card.setAttribute("data-aa-group", group.configId);

    var main = el("div", "aa-card__main");
    main.appendChild(mediaNode(group));

    var body = el("div", "aa-card__body");
    // Title links to the product (parity with plain rows), if we know its URL.
    var groupUrl = group.base && group.base.url;
    var titleEl;
    if (groupUrl) {
      titleEl = el("a", "aa-card__title");
      titleEl.href = groupUrl;
    } else {
      titleEl = el("div", "aa-card__title");
    }
    titleEl.appendChild(txt(group.model || "Custom cable"));
    body.appendChild(titleEl);

    var segs = specSegments(group.options);
    if (segs.length) {
      var spec = el("div", "aa-card__spec");
      for (var s = 0; s < segs.length; s++) {
        if (s > 0) spec.appendChild(el("span", "aa-card__dot", "&middot;"));
        spec.appendChild(el("span", "aa-card__seg", "")).appendChild(txt(segs[s]));
      }
      body.appendChild(spec);
    }
    main.appendChild(body);

    var side = el("div", "aa-card__side");
    side.appendChild(priceBlock(group.totalCents, group.originalCents, format));
    side.appendChild(qtyStepper(group.quantity || 1, "group", group.configId));
    main.appendChild(side);
    card.appendChild(main);

    // Actions: Edit (+ "Build another" — a fresh configurator for the same model), then a quiet red trash link.
    var actions = el("div", "aa-card__actions");
    var pair = el("div", "aa-actions-pair");
    var edit = el("button", "aa-link aa-link--edit");
    edit.appendChild(txt(aaT("edit")));
    edit.type = "button";
    edit.setAttribute("data-aa-action", "edit");
    edit.setAttribute("data-aa-cid", group.configId);
    pair.appendChild(edit);
    if (showKeep) {
      var keep = el("button", "aa-link aa-link--keep");
      keep.appendChild(txt(aaT("build_another")));
      keep.type = "button";
      keep.setAttribute("data-aa-action", "keep");
      keep.setAttribute("data-aa-cid", group.configId);
      pair.appendChild(keep);
    }
    actions.appendChild(pair);
    actions.appendChild(removeLink("remove-group", "data-aa-cid", group.configId, aaT("remove_title", { title: group.model || "this cable" })));
    card.appendChild(actions);
    return card;
  }

  function renderProduct(item, format) {
    var line = item.line;
    var card = el("div", "aa-card aa-card--product");
    card.setAttribute("data-aa-line", line.key);

    var main = el("div", "aa-card__main");
    main.appendChild(mediaNode(item));

    var body = el("div", "aa-card__body");
    var title;
    if (line.url) {
      title = el("a", "aa-card__title");
      title.href = line.url;
      title.appendChild(txt(displayValue(line.title)));
    } else {
      title = el("div", "aa-card__title");
      title.appendChild(txt(displayValue(line.title)));
    }
    body.appendChild(title);

    if (!line.has_default_variant && line.variant_title && line.variant_title !== "Default Title") {
      body.appendChild(el("div", "aa-card__spec", "")).appendChild(txt(displayValue(line.variant_title)));
    }
    // Visible (non _) line properties, if any.
    var pPairs = optionPairs(line.properties || {});
    if (pPairs.length) {
      var segs = specSegments(pPairs);
      var spec = el("div", "aa-card__spec");
      for (var s = 0; s < segs.length; s++) {
        if (s > 0) spec.appendChild(el("span", "aa-card__dot", "&middot;"));
        spec.appendChild(el("span", "aa-card__seg", "")).appendChild(txt(segs[s]));
      }
      body.appendChild(spec);
    }
    main.appendChild(body);

    var side = el("div", "aa-card__side");
    side.appendChild(priceBlock(lineCents(line), origCents(line), format));
    side.appendChild(qtyStepper(line.quantity || 1, "line", line.key));
    main.appendChild(side);
    card.appendChild(main);

    var actions = el("div", "aa-card__actions aa-card__actions--product");
    actions.appendChild(removeLink("remove-line", "data-aa-key", line.key, aaT("remove_title", { title: displayValue(line.title) })));
    card.appendChild(actions);
    return card;
  }

  /* ------------------------------ enhancement ----------------------------- */
  function scheduleEnhance() {
    if (enhanceQueued) return;
    enhanceQueued = true;
    window.setTimeout(function () { enhanceQueued = false; enhance(); }, 0);
  }

  function enhance() {
    var cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;
    var dataNode = document.getElementById("CartDrawer-AcfgData");
    var groupsRoot = document.getElementById("CartDrawer-AcfgGroups");
    // No data island / groups host (empty cart, or section not yet present):
    // leave the stock markup exactly as Dawn rendered it.
    if (!dataNode || !groupsRoot) { cartDrawer.classList.remove("aa-cart-enhanced"); return; }

    if (observer) observer.disconnect();
    try {
      var data = JSON.parse(dataNode.textContent || "{}");
      currentFormat = data.money_format || "";
      currentView = groupLines(data.lines || []);
      var frag = renderView(currentView, currentFormat);
      groupsRoot.innerHTML = "";
      groupsRoot.appendChild(frag);
      groupsRoot.removeAttribute("hidden");
      cartDrawer.classList.add("aa-cart-enhanced");
      showError("");
    } catch (e) {
      // Fail-safe: revert to the stock flat list, never break the drawer.
      try {
        cartDrawer.classList.remove("aa-cart-enhanced");
        groupsRoot.innerHTML = "";
        groupsRoot.setAttribute("hidden", "");
      } catch (e2) {}
      if (window.console && console.error) console.error("[acfg-cart] grouping failed; stock cart shown", e);
    } finally {
      reconnect(cartDrawer);
    }
  }

  function reconnect(cartDrawer) {
    if (!observer) {
      observer = new MutationObserver(function () { scheduleEnhance(); });
    }
    try { observer.observe(cartDrawer, { childList: true, subtree: true }); } catch (e) {}
  }

  /* ------------------------- cart mutations (AJAX) ------------------------ */
  function postCart(url, payload) {
    payload.sections = SECTIONS;
    payload.sections_url = window.location.pathname;
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error((body && (body.description || body.message)) || "Cart update failed.");
        return body;
      });
    });
  }

  function applySections(state) {
    if (!state || !state.sections) return;
    var drawerHtml = state.sections["cart-drawer"];
    if (drawerHtml) {
      var doc = new DOMParser().parseFromString(drawerHtml, "text/html");
      ["cart-drawer-items", ".cart-drawer__footer"].forEach(function (sel) {
        var src = doc.querySelector(sel), dst = document.querySelector(sel);
        if (src && dst) dst.replaceWith(src);
      });
      var cd = document.querySelector("cart-drawer");
      if (cd && typeof state.item_count === "number") cd.classList.toggle("is-empty", state.item_count === 0);
    }
    var bubbleHtml = state.sections["cart-icon-bubble"];
    if (bubbleHtml) {
      var bubble = document.getElementById("cart-icon-bubble");
      if (bubble) {
        var target = bubble.querySelector(".shopify-section") || bubble;
        var parsed = new DOMParser().parseFromString(bubbleHtml, "text/html");
        var inner = parsed.querySelector(".shopify-section");
        target.innerHTML = inner ? inner.innerHTML : parsed.body.innerHTML;
      }
    }
    // Let Dawn's own listeners sync. Source "cart-items" stops the drawer's
    // subscriber from firing a second, redundant section refetch.
    try {
      if (typeof publish === "function" && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: state });
      }
    } catch (e) {}
  }

  // Refetch + swap the drawer section after an EXTERNAL cart change (e.g. the
  // configurator's own Add to cart, which bypasses Dawn's product form and only
  // dispatches "cart:refresh"). Keeps the unified drawer's data current so a
  // configured cable shows up grouped the next time the drawer opens. Silent and
  // fail-safe: it never opens the drawer and never throws.
  function refetchDrawer() {
    var base = routeUrl("cart_url", "/cart");
    fetch(base + "?section_id=cart-drawer", { headers: { "Accept": "text/html" } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        ["cart-drawer-items", ".cart-drawer__footer"].forEach(function (sel) {
          var src = doc.querySelector(sel), dst = document.querySelector(sel);
          if (src && dst) dst.replaceWith(src);
        });
        var cd = document.querySelector("cart-drawer"), src2 = doc.querySelector("cart-drawer");
        if (cd && src2 && src2.classList) cd.classList.toggle("is-empty", src2.classList.contains("is-empty"));
        scheduleEnhance();
      })
      .catch(function () {});
  }

  function updateCart(updates) {
    if (busy) return Promise.resolve(null);
    busy = true;
    var root = document.getElementById("CartDrawer-AcfgGroups");
    if (root) root.classList.add("aa-busy");
    return postCart(routeUrl("cart_update_url", "/cart/update.js"), { updates: updates })
      .then(function (state) { applySections(state); scheduleEnhance(); return state; })
      .catch(function (err) { showError(err.message || "Could not update cart."); })
      .then(function (state) {
        busy = false;
        if (root) root.classList.remove("aa-busy");
        return state;
      });
  }

  /* -------------------------------- undo ---------------------------------- */
  function clearUndo() {
    if (undoState && undoState.timer) window.clearTimeout(undoState.timer);
    undoState = null;
    var t = document.getElementById("CartDrawer-AcfgToast");
    if (t && t.parentNode) t.parentNode.removeChild(t);
  }

  function showUndo(model, items) {
    clearUndo();
    var inner = document.querySelector("cart-drawer .drawer__inner");
    if (!inner) return;
    var toast = el("div", "aa-toast");
    toast.id = "CartDrawer-AcfgToast";
    toast.setAttribute("role", "status");
    var msg = el("span", "aa-toast__msg", "");
    msg.appendChild(txt(aaT("removed_model", { model: model || "item" })));
    var undoBtn = el("button", "aa-toast__undo");
    undoBtn.appendChild(txt(aaT("undo")));
    undoBtn.type = "button";
    undoBtn.setAttribute("data-aa-action", "undo");
    toast.appendChild(msg);
    toast.appendChild(undoBtn);
    var footer = inner.querySelector(".drawer__footer");
    if (footer) inner.insertBefore(toast, footer); else inner.appendChild(toast);

    undoState = { items: items, model: model, timer: window.setTimeout(clearUndo, UNDO_MS) };
    try { undoBtn.focus(); } catch (e) {}
  }

  function doUndo() {
    if (!undoState || busy) return;
    var items = undoState.items;
    clearUndo();
    busy = true;
    var root = document.getElementById("CartDrawer-AcfgGroups");
    if (root) root.classList.add("aa-busy");
    postCart(routeUrl("cart_add_url", "/cart/add.js"), { items: items })
      .then(function (state) {
        applySections(state);
        scheduleEnhance();
        announce("Item restored to cart.");
      })
      .catch(function (err) { showError(err.message || "Could not restore the item."); })
      .then(function () { busy = false; if (root) root.classList.remove("aa-busy"); });
  }

  /* -------------------------- edit / keep configuring --------------------- */
  function closeDrawer() {
    var cd = document.querySelector("cart-drawer");
    if (cd && typeof cd.close === "function") cd.close();
  }

  // Reopen the configurator pre-filled. If a live configurator for the same
  // model is on this page, prefill it in place; otherwise stash the option map
  // and navigate to the product page, where the engine consumes the handoff.
  function editGroup(group) {
    var model = group.model || "";
    var props = group.base && group.base.properties ? group.base.properties : {};
    var map = {};
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) { if (keys[i].charAt(0) !== "_") map[keys[i]] = props[keys[i]]; }
    // The edited group's line keys ride along so the engine can REPLACE the old
    // lines when (and only when) the re-configured cable is successfully added.
    var lineKeys = [];
    for (var lk = 0; lk < group.lines.length; lk++) { if (group.lines[lk] && group.lines[lk].key) lineKeys.push(group.lines[lk].key); }

    var roots = document.querySelectorAll("[data-acfg-root]");
    for (var r = 0; r < roots.length; r++) {
      var inst = roots[r].__acfg;
      if (inst && inst.productTitle && model &&
          String(inst.productTitle).trim() === String(model).trim() &&
          typeof inst.prefillFromLabels === "function") {
        try {
          inst.prefillFromLabels(map);
          inst._pendingReplaceKeys = lineKeys.slice();
          if (inst.viewMode === "wizard") { inst.step = 0; if (typeof inst._applyView === "function") inst._applyView(); }
          closeDrawer();
          if (typeof roots[r].scrollIntoView === "function") roots[r].scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        } catch (e) { /* fall through to navigation */ }
      }
    }
    // Cross-page handoff. The engine reads this on init and only applies it when
    // the product matches _model (so a different product is never mis-prefilled).
    try { window.sessionStorage.setItem("acfg_prefill", JSON.stringify({ model: model, props: map, replace_keys: lineKeys })); } catch (e) {}
    var url = group.base && group.base.url;
    if (url) window.location.href = url;
    else closeDrawer();
  }

  /* ------------------------------ interactions ---------------------------- */
  function findGroup(cid) {
    for (var i = 0; i < currentView.length; i++) {
      if (currentView[i].type === "group" && currentView[i].configId === cid) return currentView[i];
    }
    return null;
  }
  function findLine(key) {
    for (var i = 0; i < currentView.length; i++) {
      if (currentView[i].type === "product" && currentView[i].line.key === key) return currentView[i].line;
    }
    return null;
  }

  function onClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest("[data-aa-action]") : null;
    if (!btn) return;
    // Scope to THIS drawer's own host/toast. On /cart the page controller renders
    // identical [data-aa-action] buttons (same cart, same _config_id) into its own
    // host; without this guard this global document listener would double-fire and
    // remove/change a line twice. The page controller is scoped the same way.
    if (!btn.closest("#CartDrawer-AcfgGroups") && !btn.closest("#CartDrawer-AcfgToast")) return;
    var action = btn.getAttribute("data-aa-action");
    if (action === "undo") { e.preventDefault(); doUndo(); return; }

    var cid = btn.getAttribute("data-aa-cid");
    var key = btn.getAttribute("data-aa-key");
    var group = cid ? findGroup(cid) : null;
    var line = key ? findLine(key) : null;

    if (action === "remove-group" && group) {
      e.preventDefault();
      var items = buildUndoItems(group);
      var model = group.model;
      // The undo toast (role="status") announces the removal; no separate
      // live-region announce, or screen readers hear it twice.
      updateCart(buildRemoveUpdates(group)).then(function (state) { if (state) showUndo(model, items); });
    } else if (action === "group-inc" && group) {
      e.preventDefault();
      updateCart(buildSetQtyUpdates(group, (group.quantity || 1) + 1));
    } else if (action === "group-dec" && group) {
      e.preventDefault();
      var nq = (group.quantity || 1) - 1;
      if (nq >= 1) updateCart(buildSetQtyUpdates(group, nq));
    } else if (action === "edit" && group) {
      e.preventDefault();
      editGroup(group);
    } else if (action === "keep" && group) {
      e.preventDefault();
      // "Build another": open a FRESH configurator for this model (no prefill).
      var burl = group.base && group.base.url;
      if (burl) window.location.href = burl; else closeDrawer();
    } else if (action === "remove-line" && line) {
      e.preventDefault();
      var lineItems = [{ id: line.variant_id, quantity: line.quantity, properties: line.properties || {} }];
      var lineTitle = displayValue(line.title);
      // The undo toast (role="status") announces the removal; no double-announce.
      var u = {}; u[line.key] = 0;
      updateCart(u).then(function (state) { if (state) showUndo(lineTitle, lineItems); });
    } else if (action === "line-inc" && line) {
      e.preventDefault();
      var lu = {}; lu[line.key] = (line.quantity || 1) + 1;
      updateCart(lu);
    } else if (action === "line-dec" && line) {
      e.preventDefault();
      var ld = (line.quantity || 1) - 1;
      if (ld >= 1) { var d = {}; d[line.key] = ld; updateCart(d); }
    }
  }

  /* --------------------------------- init --------------------------------- */
  function init() {
    var cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;
    document.addEventListener("click", onClick);
    document.addEventListener("cart:refresh", refetchDrawer);
    reconnect(cartDrawer);
    enhance();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

